import Core from "@/core/Core";
import StoresDAO from "@/domains/Stores/StoresDAO";
import Store from "@/domains/Stores/Store";
import RpcDataPagination from "@/core/services/Rpc/RpcDataPagination";
import RpcDataSort from "@/core/services/Rpc/RpcDataSort";

import type Partner from "@/domains/Partners/Partner";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";

/**
 * Сервис для работы со складами
 */
class StoresService {

	/** Опции фильтров реестра. Поле имени совпадает с filter-identity колонки в гриде. */
	static IS_DRAFT_OPTIONS = [
		{ id: 1, isDraft: true,  name: 'Черновик' },
		{ id: 2, isDraft: false, name: 'Согласованный' },
	];

	static HAS_TRANSPORT_ZONE_OPTIONS = [
		{ id: 1, hasTransportZone: true,  name: 'С зоной' },
		{ id: 2, hasTransportZone: false, name: 'Без зоны' },
	];


	static async getListPaginated(
		params?: Parameters<typeof StoresDAO.getList>[0] & {
      pagination?: RpcDataPagination;
      sort?: RpcDataSort;
    }
	): Promise<{ data: Store[]; pagination: RpcDataPagination }> {
		const { pagination, sort, ...filters } = params ?? {};
		const response = await StoresDAO.getList(filters, pagination, sort);

		return {
			data: Core.utils.hydrateArray(response.data, Store.fromServer) as Store[],
			pagination: response.meta.pagination,
		}
	}

	static async getList(
    params?: Parameters<typeof StoresDAO.getList>[0]
  ): Promise<Store[]> {
		return (await this.getListPaginated(params)).data;
	}

	static async  get(id: number): Promise<Store | null> {
		const response = await StoresDAO.get(id);

		return Core.utils.hydrate(response.data, Store.fromServer);
	}

	/** ------- CRUD ------- */

	// NOTE BENE! 27.01.26 Здесь возвращается объект
	static async add(store: Store, partnerId: number): Promise<boolean> {
		const response = await StoresDAO.add(store, partnerId);

		if (response.ok) {
			Core.showNotification.success('Склад успешно добавлен');
		}

		return response.ok;
	}

	static async update(store: Store): Promise<boolean> {
		const response = await StoresDAO.update(store);

		if (response.ok) {
			Core.showNotification.success('Склад успешно обновлен');
		}

		return response.ok;
	}

	static async delete(id: number): Promise<boolean> {
		const response = await StoresDAO.delete(id);

		if (response.ok) {
			Core.showNotification.success('Склад успешно удален');
		}

		return response.ok;
	}

	/**
	 * Удаление с подтверждением. Используется из контекстного меню склада.
	 * Старый delete(id) оставлен для массовых операций без UX.
	 */
	static async deleteWithConfirmation(store: Store): Promise<boolean> {
		const confirmed = await Core.dialogs.confirm(
			'Удалить этот склад?',
			`Склад ${store.name}`,
		);

		if (!confirmed) return false;

		return await this.delete(store.id);
	}

	/** ------- вызов диалога создания/редактирования склада ------- */

	static async createStore(
		partner: Partner,
		onStoreCreated: (data: any) => void
	) {
		const newStore = Store.fromPartner(partner);
		await this.showStoreDialog(newStore, onStoreCreated, null);
	}

	// перегрузки
	static async editStore(id: number): Promise<void>;
	static async editStore(
		store: Store,
		onStoreUpdated?: (data: any) => void,
	): Promise<void>;

	// реализация
	static async editStore(
		idOrStore: number | Store,
		onStoreUpdated?: (data: any) => void,
	): Promise<void> {
		const store = typeof idOrStore === 'number'
			? await this.get(idOrStore)
			: idOrStore;

		if (!store) return;

		await this.showStoreDialog(store, null, onStoreUpdated);
	}


	// без перегрузок
	static async showStoreDialog(
		store: Store,
		onStoreCreated?: (data?: any) => void,
		onStoreUpdated?: (data?: any) => void,
	): Promise<void> {
		Core.dialogs.StoreCard.show(
			{
				id: store.id,
				store,
			},
			{
				[Store.Events.Created]: onStoreCreated,
				[Store.Events.Updated]: onStoreUpdated,
			}
		);
	}


	/**
	 * Форматирует список складов, так как код дублируется в трех компонентах минимум,
	 * данный метод вынесен в сервис
	 *
	 * @param {Array} stores - массив складов с полями address, workingTime, id, transportZone
	 * @param {number|null} [transportZoneId=null] - ID зоны для фильтрации
	 * @returns {Array} отформатированные склады { name, workingTime, id, transportZoneId }
	 */
	static formatStoresOptions(
		stores: Array<Store | null>,
		transportZoneId: number | null = null
	): Array<any>{

		if (!stores?.length) {
			return [];
		}

		const mapped: any[] = stores.map(store => ({
			name: store.address?.stringRepresentation ?? store.name ?? 'Без названия',
			workingTime: store.workingTime ?? null,
			id: store.id,
			transportZoneId: store.transportZone?.id,
		}));

		/**
		 * После победы в тендере поставщик формирует сборочный лист.
		 * В него автоматически подставляется склад, указанный в тендерной заявке.
		 * Однако фактический склад отгрузки на момент подачи заявки может быть неизвестен и
		 * поставщик указывает наиболее вероятный склад.
		 * Поэтому даем ему возможность изменить склад, но только с той же трансопртной зоной */
		if (transportZoneId !== null) {
			return mapped.filter(store => store.transportZoneId === transportZoneId);
		}

		return mapped;
	}

  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (store: Store) => { return StoresService.editStore(store); },
        null,
        false,
        false,
        true,
      ),

      new ContextMenuItem(
        'История',
        'history',
        (store: Store) => {
          Core.dialogs.FeedDialog.show({
            entityTypeName: 'store',
            entityId:       store.id,
            showAddComment: false,
          });
        },
        null,
        false,
        false,
      ),

      new ContextMenuItem(
        'Пометить как черновик',
        'draw',
        (store: Store) => {
          const localStore = Core.utils.cloneDeep(store);
          localStore.isDraft = true;

          return StoresService.update(localStore);
        },
        (store: Store) => store.getReasonCannotSetAsDraft(),
        false,
        true,
      ),

      new ContextMenuItem(
        'Утвердить',
        'check_circle',
        (store: Store) => {
          const localStore = Core.utils.cloneDeep(store);
          localStore.isDraft = false;

          return StoresService.update(localStore);
        },
        (store: Store) => store.getReasonCannotApprove(),
        false,
        true,
        true,
      ),

      new ContextMenuItem(
        'Удалить',
        'delete',
        (store: Store) => { return StoresService.deleteWithConfirmation(store); },
        null,
        false,
        true,
      ),
    ];
  }
}

export default StoresService;
